import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import parts from 'redux/parts';
import maker from 'redux/maker';
import model from 'redux/model';
import brand from 'redux/brand';
import category from 'redux/category';
import user from 'redux/user';

import { history } from 'router';

import BrandLogo from 'components/atoms/BrandLogo';
import ModelImages from 'components/atoms/ModelImages';
import Table from 'components/molecules/Table';
import TableSearch from 'components/molecules/TableSearch';
import Field from 'components/molecules/Field';
import PurchaseButton from 'components/molecules/PurchaseButton';
import CartModal from 'components/molecules/CartModal';
import ScrapeGuard from 'components/molecules/ScrapeGuard';
import useCart from 'hooks/useCart';
import usePagination from 'hooks/usePagination';
import { PART_CODE_MIN_LENGTH } from 'utils/constants';
import { parseQuery } from 'utils/query';

import { dropDown } from './tableColumns';
import { isScoped } from './scope';
import newIcon from 'assets/icons/new.png';

import './index.scss';

const ListPrices = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useRef(parseQuery(location.search));
  const { t } = useTranslation('common');

  const filteredParts = useSelector(parts.selectors.selectAll);
  const loading = useSelector(parts.selectors.getLoadingState);
  const { total_entries: total } = useSelector(parts.selectors.getMetaData);

  const currentUser = useSelector(user.selectors.getCurrentUser);
  const currentMaker = useSelector((state) => maker.selectors.selectById(state, params.current.maker_id));
  const currentModel = useSelector((state) => model.selectors.selectById(state, params.current.model_id));
  const currentBrand = useSelector((state) => brand.selectors.selectById(state, params.current.brand_id));
  const currentCatergory = useSelector((state) => category.selectors.selectById(state, params.current.category_id));

  const {
    onSubmit,
    updateRecords,
    onModalClose,
    resetCartItems,
    cartItems,
    cartLoader,
    cartAlert,
    cartTotal,
  } = useCart();

  const { pagination, onPaginationChange, reset } = usePagination();

  useEffect(() => {
    params.current = parseQuery(location.search);
    /* Nothing to narrow the listing by means the request would come back with
       every part the client can see, so the screen never makes it - see ./scope.
       The client goes back to the code search, which is the only way to reach
       the listing without picking a model, brand or category first. `replace`
       so the back button does not land on the same empty listing again. */
    if (!isScoped(params.current)) {
      history.replace('/search_by_code');
      return;
    }
    /* A different model or brand is a different list, so it starts at page
       one - while keeping whatever page size the client picked. */
    const { current, pageSize } = reset();
    dispatch(parts.thunks.filterParts({ params: { ...params.current, page: current, per_page: pageSize } }));
    dispatch(maker.thunks.fetch());
    resetCartItems();
  }, [dispatch, location.search, resetCartItems, reset]);

  const onChange = useCallback((identifier, value) => {
    if (value) {
      const newParams = queryString.stringify({ ...params.current, [identifier]: value });
      history.push(`/parts?${newParams}`);
    }
  }, []);

  const onPageChange = useCallback(
    (nextPagination) => {
      // Quantities typed on the page being left are carried over, as before.
      const { current, pageSize } = onPaginationChange(nextPagination);
      dispatch(
        parts.thunks.filterParts({
          params: { ...params.current, page: current, per_page: pageSize },
          selectedItems: cartItems,
        }),
      );
      window.scrollTo(0, 0);
    },
    [dispatch, cartItems, onPaginationChange],
  );

  // A code / part number search spans every model, so there is nothing to put in
  // the model column - it is dropped rather than rendered empty.
  const isCodeSearch = params.current.type === 'code';

  /* The term goes into the URL like every other filter, so the effect above
     refetches and resets to page one on its own. Filtering here in the browser
     would only ever search the page already on screen.

     On a code search the box has to replace part_code rather than add `name`:
     the two filters are ANDed by the API, so a second search would look for the
     previous code AND the new term and always come back empty. Everywhere else
     the box filters the listing by arabic name, as before. */
  const onSearch = useCallback(
    (value) => {
      const next = { ...params.current };
      const key = isCodeSearch ? 'part_code' : 'name';
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
        /* Clearing a code search has to drop `type` with it. Leaving type=code
           behind asked the API for a code search with no code - an empty table
           the client could not get out of, still under the code layout and its
           5-character rule. */
        if (isCodeSearch) delete next.type;
      }
      /* Clearing the only filter there was leaves no listing to show, so the
         client goes straight back to the code search rather than through a
         listing the guard above would bounce them out of anyway. */
      if (!isScoped(next)) {
        history.push('/search_by_code');
        return;
      }
      history.push(`/parts?${queryString.stringify(next)}`);
    },
    [isCodeSearch],
  );

  /* Empty string rather than undefined: a key of `undefined` is shared by every
     term-less state, so the box would skip the remount the key exists to force. */
  const searchTerm = (isCodeSearch ? params.current.part_code : params.current.name) || '';

  const columns = [
    {
      title: 'product-name',
      dataIndex: 'arabic_name',
      render: (text, record) => (
        <React.Fragment>
          {text} {record.newly_updated && <img src={newIcon} alt="New" />}
        </React.Fragment>
      ),
      width: '22%',
    },
    {
      title: 'model',
      dataIndex: 'show_model_names',
      // Same name as before, now with each model's image above it. When the table
      // is filtered to one model we show just that one, matching the name this
      // used to render; otherwise we list every model the part fits. `value` still
      // gates the unfiltered case - show_model_names is only sent when asked for.
      // No code-search guard here: that search drops the column outright, below.
      render: (value, record) => {
        const models = currentModel
          ? [{ name: currentModel.name, image_url: currentModel.image_url }]
          : (value && record.models) || [];
        return <ModelImages models={models} />;
      },
      width: '10%',
      align: 'center',
    },
    {
      title: 'brand',
      dataIndex: 'brand_image',
      // What lets the logo size itself to the row rather than set it - see BrandLogo.
      className: 'brand-cell',
      render: (value, record) => <BrandLogo src={value} name={record.brand_name} />,
      width: '8%',
      align: 'center',
    },
    {
      title: 'country',
      dataIndex: 'country_name',
      width: '6%',
      align: 'center',
    },
    {
      title: 'remark',
      dataIndex: 'remark',
      width: '6%',
      align: 'center',
    },
    {
      title: 'price',
      dataIndex: 'price_and_promotion',
      render: (value, record) => {
        return (
          <>
            {value.promotion && currentUser.show_promotions && record['on_promotion?'] ? (
              <>
                <s>{Math.floor(value.price)?.toLocaleString()}</s>
                <br></br>
                {Math.floor(value.promotion)?.toLocaleString()}
              </>
            ) : (
              Math.floor(record.price)?.toLocaleString()
            )}
          </>
        );
      },
      width: '7%',
      align: 'center',
    },
    {
      title: 'quantity',
      dataIndex: ['in_cart', 'quantity'],
      isEditable: true,
      type: 'number',
      width: '8%',
      align: 'center',
    },
    {
      title: 'total',
      dataIndex: 'total',
      render: (_, record) => {
        return (
          (
            Math.floor(
              currentUser.show_promotions && record['on_promotion?']
                ? record.price_and_promotion?.promotion
                : record.price,
            ) * record.in_cart.quantity
          )?.toLocaleString() || 0
        );
      },
      width: '8%',
      align: 'center',
    },
    {
      title: 'images',
      dataIndex: 'images',
      type: 'images',
      width: '12%',
      align: 'center',
    },
    {
      title: 'code',
      dataIndex: 'code',
      width: '10%',
      align: 'center',
    },
  ].filter((column) => !(isCodeSearch && column.title === 'model'));

  return (
    <div
      title={`${t('list-prices')} ${currentModel?.name ? `/ ${currentModel.name}` : ''} ${
        currentMaker?.name ? `/ ${currentMaker.name}` : ''
      } ${currentBrand?.name ? `/ ${currentBrand.name}` : ''} ${
        currentCatergory?.name ? `/ ${currentCatergory.name}` : ''
      }`}
    >
      <CartModal alert={cartAlert} onCancel={onModalClose} total={cartTotal} />
      <div className="parts-toolbar">
        {!isCodeSearch && (
          <div className="custom-drop-down">
            <Field
              onChange={onChange}
              filteredState={{ maker_id: params.current.maker_id }}
              value={parseInt(params.current.model_id) || undefined}
              {...dropDown}
              localePath="table-fields"
              name={undefined}
            />
          </div>
        )}
        <TableSearch
          /* Keyed so the box remounts with the new term when the URL changes:
             defaultValue alone is only read on the first render, which left the
             previous code sitting in the box after a second search. */
          key={`${isCodeSearch ? 'code' : 'name'}:${searchTerm}`}
          defaultValue={searchTerm}
          placeholder={isCodeSearch ? t('search-by-code') : undefined}
          minLength={isCodeSearch ? PART_CODE_MIN_LENGTH : undefined}
          onSearch={onSearch}
        />
        <PurchaseButton buttonTitle={t('add-to-cart')} sideLabel={t('add-to-cart-side-label')} onClick={onSubmit} />
      </div>
      {/* Only the table is wrapped. The filters and the search box above stay
          up either way - they carry no catalogue data, and leaving them there
          keeps the screen recognisable rather than looking broken. */}
      <ScrapeGuard>
        <Table
          columns={columns}
          dataSource={filteredParts}
          updateDataSource={updateRecords}
          loading={loading || cartLoader}
          rowKey="id"
          localePath="table-fields"
          pagination={{ ...pagination, total }}
          onChange={onPageChange}
          /* On the pager's own row rather than a row of its own below it - the
             button is what a dealer reaches for after setting quantities, so it
             has to stay in view without costing the table a row of parts. */
          footerExtra={
            <PurchaseButton buttonTitle={t('add-to-cart')} sideLabel={t('add-to-cart-side-label')} onClick={onSubmit} />
          }
        />
      </ScrapeGuard>
    </div>
  );
};

export default ListPrices;
